import { z } from "zod";

export const CAMP_DATA_RUN_DOC_SCHEMA_VERSION = "camp-data.runDoc.v1" as const;
export const CAMP_DATA_GOLDEN_DOC_SCHEMA_VERSION = "camp-data.goldenDoc.v1" as const;

/**
 * `dormant` (KB-3448): the harvest found zero sessions AND the source itself was quoted as off-season
 * (`execution.observed.zeroSessionsOutcome === "evidenced"`, sub-kind `off-season-suspected`). It is
 * NOT a success — nothing was harvested and the camp's data is not current — and NOT a failure.
 */
export type CampDataRunStatus = "in_progress" | "success" | "fail" | "blocked" | "unknown" | "dormant";
export type CampDataRegressionStatus = "PASS" | "FAIL" | "UNKNOWN";

export const CampDataRunDocV1Schema = z.object({
  schemaVersion: z.literal(CAMP_DATA_RUN_DOC_SCHEMA_VERSION),

  campSlug: z.string().min(1),
  runId: z.string().min(1),

  createdAtIso: z.string().min(1),
  updatedAtIso: z.string().min(1),

  note: z.string().optional(),

  status: z.enum(["in_progress", "success", "fail", "blocked", "unknown", "dormant"]),

  /**
   * KB-1440: a "preview (no write)" run — reconcile/observed→offers ran report-only and NOTHING
   * was written to live `offers` / `campLocations`. Gates the PREVIEW badge in Run history.
   */
  previewMode: z.boolean().optional(),

  /**
   * KB-1966: a WATCH run — a `ingestionMode="watch"` dormant-camp audit that harvests NO sessions
   * and touches NO offers; it only audits the org's pages + vendor feeds against a stored baseline
   * (`campDataWatchState/<slug>`) and reports signals of a reopening. `status:"signal"` gates the
   * amber SIGNAL badge in Run history and mirrors into `campDataWatcherAlerts/<slug>` for the Inbox.
   */
  watch: z
    .object({
      status: z.enum(["quiet", "signal"]),
      level: z.union([z.enum(["critical", "warn", "info"]), z.null()]).optional(),
      isFirstRun: z.boolean().optional(),
      signals: z.array(
        z
          .object({
            type: z.string(),
            severity: z.enum(["critical", "warn", "info"]),
            message: z.string(),
            evidence: z.record(z.unknown()).optional(),
            detectedAtIso: z.string().optional(),
          })
          .passthrough(),
      ),
      checked: z
        .object({ pages: z.number().optional(), vendorProbes: z.number().optional() })
        .passthrough()
        .optional(),
      /**
       * KB-1323 B — how the run's fetches went. `failed` means nothing was read and the run doc's
       * `status` is `fail`; `partial` keeps `status: "success"` while the org's `lastWatchRun.status`
       * says `partial`. Absent on runs before KB-1323, which could not tell a dead site from a quiet one.
       */
      fetch: z
        .object({
          outcome: z.enum(["ok", "partial", "failed"]),
          targets: z.number().optional(),
          failed: z.array(z.object({ target: z.string(), error: z.string() }).passthrough()).optional(),
        })
        .passthrough()
        .optional(),
      summaryMd: z.string().optional(),
    })
    .passthrough()
    .optional(),

  execution: z
    .object({
      /**
       * Pipeline/script-level window. Note: `publishRunToFirestore` may overwrite these based on pipeline artifacts.
       */
      runStartedAtIso: z.string().optional(),
      runEndedAtIso: z.string().optional(),
      /**
       * Wall-clock window for "harvesting" (queue worker claim → completion).
       * Written by the queue worker and intended to be stable even if pipeline timestamps are overwritten.
       */
      harvestStartedAtIso: z.string().optional(),
      harvestEndedAtIso: z.string().optional(),
      /**
       * KB-2559 — the adapter-native observed→offers writer's own phase window, written by
       * `run-camp-data-pipeline.cjs` (via run.json's `execution["observed-to-offers"]`) and
       * carried through by `publishRunToFirestore.cjs`. Before this, a healthy adapter-native
       * run that proposed N offers had NO phase evidence it executed at all — `run`/`harvest`
       * were the only timed phases on every such run, healthy or not.
       */
      observedToOffersStartedAtIso: z.string().optional(),
      observedToOffersEndedAtIso: z.string().optional(),
      observedToOffersStatus: z.string().optional(),
      stages: z.record(z.object({ startedAtIso: z.string().optional(), endedAtIso: z.string().optional() })).optional(),
      /**
       * Observed-phase outcome. `observed-sessions-from-recon.cjs` has written this object since
       * KB-1605 (`observeNA`), but it was never declared here — and `execution` has no
       * `.passthrough()`, so Zod has been silently stripping the whole key at the client boundary.
       *
       * KB-2296 adds `zeroSessionsCause`, which is the field that makes a zero-session run
       * attributable: `deadline` (a wedge) vs `transient-fetch` (retry next run) vs `empty-source`
       * (the only one that may mean adapter drift). Conflating those three is what made one
       * scheduling bug read as four adapter bugs, so the distinction has to survive the parse.
       *
       * KB-2305 adds a fourth, `not-yet-published` — the source is reachable and has simply not
       * scheduled its season. THIS ENUM IS THE CONTRACT, not a description of it: the writer is
       * `observed-sessions-from-recon.cjs`, and a cause it emits that is missing here fails the
       * whole `safeParse` and blanks the run row for exactly the runs the diagnostic exists to
       * explain. That is the same defect class KB-2296's review caught (an `error` written as a
       * string where this schema declares an object). Extend this enum in the same change as the
       * writer, never after it.
       *
       * KB-2627 adds a fifth, `listing-window-suppressed` — the adapter read a real listing and
       * KB-2081's listing-window guard removed every row it would have emitted, so the zero is a
       * per-camp config gap rather than an empty source. Adding it here is not bookkeeping: the
       * warning above describes exactly how this ticket would otherwise have failed. The cause
       * exists to make `childrens-art-classes` visible after 19 runs of reading as `empty-source`,
       * and an unlisted cause blanks the run row — so shipping the writer without this line would
       * have made the camp LESS visible than before, in the one surface built to explain it.
       *
       * KB-4580 adds a sixth, `proven-empty` — the adapter read the vendor's own listing, the vendor
       * stated a zero count or empty-state, and a positive control on the same endpoint in the same
       * run read rows. Its proof rides on `zeroSessionsEvidence.provenEmpty`. Added here in the same
       * change as the writer, for the reason above.
       *
       * KB-4983 adds `pinned-source-changed`, `pinned-source-missing`, `pinned-source-unreadable` and
       * `pinned-source-superseded` — the camp's sessions are a checked-in transcription bound to a
       * vendor file's sha256, and that file changed, is gone, was refused by its host, or has a newer
       * upload beside it.
       * Added in the same change as the writer, for the reason above.
       */
      observed: z
        .object({
          endedAt: z.string().optional(),
          observeNA: z.string().optional(),
          zeroSessionsCause: z
            .enum([
              "deadline",
              "transient-fetch",
              "empty-source",
              "not-yet-published",
              "listing-window-suppressed",
              "proven-empty",
              "pinned-source-changed",
              "pinned-source-missing",
              "pinned-source-unreadable",
              "pinned-source-superseded",
            ])
            .optional(),
          /**
           * KB-2965 — the evidenced-vs-unexplained split for a zero-session run, and the quoted
           * source text that justifies it. `zeroSessionsCause` says WHY the harvest was empty from
           * the adapter's signals; this pair says whether that emptiness is EXPLAINED, derived
           * centrally from the run's cached artifacts (`scripts/camp-data/_observed/zeroSessionEvidence.cjs`).
           *
           *   evidenced   — a positive, quoted source match. Sub-kind `off-season-suspected` (the
           *                 source is fine, no sessions) or `vendor-error` (the source is broken).
           *   unexplained — the DEFAULT for an empty-source harvest with no derivable evidence. This
           *                 is the loud one, where a drifted adapter lands (the KB-456 fake-green
           *                 guard: absence of evidence is the alarm, never absence of sessions).
           *
           * Same contract warning as `zeroSessionsCause` above: THIS ENUM IS THE CONTRACT. A value
           * the writer emits that is missing here fails the whole safeParse and blanks the run row.
           * `zeroSessionsEvidence` is present whenever the deriver quoted something — KB-2986: that
           * is no longer only the `evidenced` case; an `unexplained` run can carry a quote too (e.g.
           * an empty-search-result banner — loud on purpose, since it is not positive evidence of an
           * empty season, but not silent about why). `.passthrough()` on it keeps a future sub-field
           * forward-compatible without a schema round-trip.
           */
          zeroSessionsOutcome: z.enum(["evidenced", "unexplained"]).optional(),
          zeroSessionsEvidence: z
            .object({
              subKind: z.enum(["off-season-suspected", "vendor-error"]).optional(),
              evidenceText: z.string().optional(),
              sourceUrl: z.string().nullable().optional(),
              quotedSnippet: z.string().optional(),
              // KB-3448 — the classifier's dormancy opt-in (emitted from KB-3524). The publisher writes
              // it only as `true`; a run is `dormant` only when it is.
              dormancyEligible: z.boolean().optional(),
              offSeasonBasis: z.string().optional(),
              // KB-4580 — what a `proven-empty` run read, and the control that shows the request works.
              provenEmpty: z
                .object({
                  listings: z.array(
                    z.object({ url: z.string(), selector: z.string(), statement: z.string() }).passthrough(),
                  ),
                  control: z
                    .object({
                      url: z.string(),
                      selector: z.string(),
                      rowsRead: z.number(),
                      statement: z.string().optional(),
                    })
                    .passthrough(),
                })
                .passthrough()
                .optional(),
            })
            .passthrough()
            .optional(),
          observedBudgetMs: z.number().optional(),
        })
        .optional(),
    })
    .optional(),

  proposals: z
    .object({
      blocked: z.boolean(),
      reason: z.string().optional(),
      counts: z.object({ location: z.number(), variation: z.number() }).optional(),
      summaryMd: z.string().optional(),
    })
    .optional(),

  reconcile: z
    .object({
      counts: z
        .object({
          desired: z.number(),
          matched: z.number(),
          missing: z.number(),
          ambiguous: z.number(),
          extras: z.number(),
        })
        .optional(),
      summaryMd: z.string().optional(),
      /** Raw `finalized_output.txt` content (wrapped-curly table), truncated for safety. */
      finalizedOutputText: z.string().optional(),
      /**
       * Optional pre-rendered RowFormat text suitable for org-mode bulk add.
       * This is intentionally stored as a string (not a huge JSON blob) to avoid Firestore doc size issues.
       */
      missingRowFormatText: z.string().optional(),
      /** Convenience: unique locationIds present in missingRowFormatText. */
      missingLocationIds: z.array(z.string().min(1)).optional(),
      /** Optional fingerprint of the reconcile missing plan used to generate missingRowFormatText. */
      missingPlanFingerprint: z.string().optional(),
      apply: z
        .object({
          summaryMd: z.string().optional(),
          counts: z
            .object({
              candidates: z.number(),
              updatesProposed: z.number(),
              skippedNoChange: z.number(),
              updatedOffers: z.number(),
              failedOffers: z.number(),
            })
            .optional(),
        })
        .optional(),
      review: z
        .object({
          summaryMd: z.string().optional(),
          counts: z
            .object({
              candidates: z.number(),
              proposals: z.number(),
              displayTitle: z.number(),
              url: z.number(),
              regUrl: z.number(),
            })
            .optional(),
          applySummaryMd: z.string().optional(),
          appliedCounts: z
            .object({
              updatedOffers: z.number(),
              failedOffers: z.number(),
            })
            .optional(),
        })
        .optional(),
    })
    .optional(),

  /**
   * KB-1532: the ingestion-mode-aware run outcome, written by `publishRunToFirestore.cjs`
   * (`metrics: lastRunMetrics`). This is sourced from the MATCH layer (not reconcile-output1),
   * so it is correct for both legacy and adapter-native camps — the run-history "What changed"
   * pills read it instead of the key-skewed `reconcile.counts`. Previously this field was written
   * to the doc but stripped here on parse, so the UI couldn't see it.
   *
   * `details.created` / `details.updated` are session-write counts; the pipeline does not
   * auto-apply sessions today, so they are currently always 0 (the pills only render them when > 0).
   * `{loc,prog}Recon.proposals` are the catalog-blocked counts (MISSING_* + AMBIGUOUS_*).
   */
  metrics: z
    .object({
      durationMs: z.number().optional(),
      durationDisplay: z.string().optional(),
      details: z
        .object({
          sessions: z.number().optional(),
          /**
           * KB-3341 — `sessions` split by whether each session has still to happen. The digest's
           * `detectRowcountDrop` judges a roll-off vs an adapter break on these.
           *
           * ⚠️ Registered here AHEAD of the writer on purpose. This object has no `.passthrough()`,
           * so Zod DROPS any key it does not name — silently, with no type error and no test
           * failure, because the render tests hand-build the object and bypass the parse. That exact
           * defect has recurred four times in this one schema (KB-1532 / KB-1561 / KB-1566 /
           * KB-1660) and is item 29 on this repo's own review checklist. The producer half of
           * KB-3341 is not in this PR; landing the key first means the writer cannot trip it.
           */
          sessionsFuture: z.number().optional(),
          sessionsPast: z.number().optional(),
          sessionsUndated: z.number().optional(),
          /**
           * KB-3633 — how many of `sessions` carry a non-empty `locationText`.
           *
           * NCSSM harvested 84 of 84 with an empty one for three weeks under `status: success`, and
           * no artifact on the run doc said so: the observed QC summary counted title, date and
           * price coverage and not location, so the field that had gone blank was the one field
           * with no counter. A blank can be honest — some vendor pages state no campus — so this
           * is a number to read, not a gate. `undefined` (not `0`) on any run whose observe layer
           * produced no summary: zero would assert "no session stated a location" about a run that
           * never looked, the KB-3186 lesson one field over.
           *
           * Registered here AHEAD of nothing — the writer ships in the same PR — but registered
           * first in the same spirit as the KB-3341 block above: this object has no
           * `.passthrough()`, so an unnamed key is dropped silently at the boundary.
           */
          sessionsWithLocation: z.number().optional(),
          matched: z.number().optional(),
          updated: z.number().optional(),
          created: z.number().optional(),
          issues: z.number().optional(),
        })
        .optional(),
      locRecon: z
        .object({
          atIso: z.string().optional(),
          locations: z.number().optional(),
          matched: z.number().optional(),
          proposals: z.number().optional(),
          issues: z.number().optional(),
        })
        .optional(),
      progRecon: z
        .object({
          atIso: z.string().optional(),
          programs: z.number().optional(),
          matched: z.number().optional(),
          proposals: z.number().optional(),
          issues: z.number().optional(),
        })
        .optional(),
      /**
       * KB-3594 — the pipeline's Firestore read meter (docs/camp-data/pipeline-architecture-and-run-doc-schema.md
       * §3b). Declared so this object's strip-by-default parse keeps it; nothing renders it yet.
       * `total` is null whenever `status` is "incomplete" — a null total is "unknown", never zero.
       */
      firestoreReads: z
        .object({
          meterVersion: z.number().optional(),
          status: z.string().optional(),
          total: z.number().nullable().optional(),
          totalLowerBound: z.number().nullable().optional(),
        })
        .passthrough()
        .optional(),
    })
    .optional(),

  operatorActions: z
    .array(
      z.object({
        kind: z.string().min(1),
        atIso: z.string().min(1),
        userId: z.string().optional(),
        note: z.string().optional(),
        meta: z.record(z.any()).optional(),
      })
    )
    .optional(),

  qc: z
    .object({
      regressionStatus: z.enum(["PASS", "FAIL", "UNKNOWN"]).optional(),
      failuresCount: z.number().nullable().optional(),
      warningsCount: z.number().nullable().optional(),
      diffSummaryMd: z.string().optional(),
      specMonFriViolationsCount: z.number().nullable().optional(),
    })
    .optional(),

  /**
   * Orchestrator-level error summary (e.g. queue worker / pipeline exit code).
   * This is intentionally separate from run.json artifacts so we can preserve failures
   * even when the pipeline dies before generating summaries.
   */
  error: z
    .object({
      message: z.string().optional(),
      exitCode: z.number().nullable().optional(),
      logTail: z.string().optional(),
      logsGcsPath: z.string().optional(),
      jobId: z.string().optional(),
      workerId: z.string().optional(),
      atIso: z.string().optional(),
    })
    .optional(),

  artifacts: z
    .object({
      runJsonRelPath: z.string().optional(),
      reconJsonRelPath: z.string().optional(),
      observedJsonlRelPath: z.string().optional(),
      matchReportRelPath: z.string().optional(),
      output1RelPath: z.string().optional(),
      finalizedOutputRelPath: z.string().optional(),
      blockedSessionsReportRelPath: z.string().optional(),
    })
    .optional(),

  /**
   * KB-1566 — the server-computed canonical reviewer counts (`deriveRunCounts.cjs`, persisted at
   * publish). MUST be carried through this parse: every reviewer surface reads these via
   * `readRunCounts`, and the Run-details "What this run changed" chip reads the PARSED doc
   * (`selectedParsed`). Without this field Zod strips `derived`, so that chip silently falls back to
   * the raw per-session counts (e.g. "add 13 · disambiguate 3" instead of the canonical
   * "add 2 · disambiguate 1"). Optional + passthrough: legacy docs lack it and the reader normalizes
   * defensively; passthrough keeps forward-compat with new derived fields.
   */
  derived: z
    .object({
      schemaVersion: z.string().optional(),
      sessionsFound: z.number().nullable().optional(),
      sessionsMatched: z.number().optional(),
      sessionsBlocked: z.number().optional(),
      blockedByReason: z
        .object({
          missingLocation: z.number().optional(),
          ambiguousLocation: z.number().optional(),
          missingVariation: z.number().optional(),
          ambiguousVariation: z.number().optional(),
        })
        .optional(),
      programsToCreate: z.number().optional(),
      programsToDisambiguate: z.number().optional(),
      locationsToCreate: z.number().optional(),
      locationsToDisambiguate: z.number().optional(),
      matchedByVariation: z.record(z.number()).optional(),
      // KB-3839 — products the org's vendor-product map withheld; the cards ride through passthrough.
      productsToMap: z.number().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-1561 — catalog-vs-observed coverage (server-derived `deriveCoverage`, persisted on the run doc).
   * MUST be carried through this parse so the run-details header can render the catalog side
   * ("We have N · M match observed · K not seen") alongside the scraped side; without this field Zod
   * strips it (top-level schema has no passthrough). Passthrough + optional: legacy/un-backfilled docs
   * lack it and the UI degrades to the observed side only.
   */
  coverage: z
    .object({
      schemaVersion: z.string().optional(),
      locationsResolved: z.boolean().optional(),
      catalogSessionCount: z.number().nullable().optional(),
      observedSessionCount: z.number().nullable().optional(),
      catalogNotObserved: z.number().optional(),
      catalogNotObservedSample: z
        .array(
          z
            .object({
              variationKey: z.string().optional(),
              startDateIso: z.string().optional(),
              displayTitle: z.string().optional(),
            })
            .passthrough(),
        )
        .optional(),
      observedNotInCatalog: z.number().optional(),
      notSeenGap: z.number().nullable().optional(),
      underObservedThresholdPct: z.number().optional(),
      underObservedWarning: z.boolean().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-1647 — the PAST-partition mirrors of `derived` / `coverage` (sessions whose resolved endDate is
   * before today). MUST be declared here or the top-level schema (which has NO passthrough — see the
   * `coverage` note above) STRIPS them, and the Run-details "Past sessions" accordion silently never
   * renders even though the server persisted the data. Passthrough + optional, exactly like
   * `derived`/`coverage`: the readers (`readRunCountsPast` / `readCoveragePast`) normalize the fields
   * defensively, and legacy / no-past docs simply lack them (→ no accordion). This was the bug: KB-1647
   * added the accordion + readers + the server-side `derivedPast`/`coveragePast` fields but not these two
   * schema keys, so Zod dropped them from `selectedParsed` and the accordion never had data to show.
   */
  derivedPast: z.object({ schemaVersion: z.string().optional() }).passthrough().optional(),
  coveragePast: z.object({ schemaVersion: z.string().optional() }).passthrough().optional(),
  /**
   * KB-1676 — the WHOLE-RUN "WHAT THIS RUN CHANGED" breakdown (Updated / Availability / Pricing / Reg. status
   * / Other) + a capped member list carrying each change's field/from/to. MUST be declared here or the
   * top-level schema (NO passthrough) STRIPS it and the breakdown tiles silently never render — the same trap
   * the `coverage` / `derivedPast` notes above document. Passthrough + optional: the reader (`readOfferUpdates`)
   * normalizes defensively, and legacy / no-change docs simply lack it (→ the plain "updated N" chip).
   *
   * KB-1685 — the new `normalization` count and each member's `suspectMatch` flag ride through the SAME
   * `.passthrough()` (they are inner keys of `offerUpdates` / its `members`, not new TOP-LEVEL fields), so no
   * extra top-level declaration is needed — the strip only bites undeclared keys of the ROOT run doc.
   */
  offerUpdates: z.object({ updated: z.number().optional() }).passthrough().optional(),
  /**
   * KB-1689 — the per-field CONTENT-parity scorecard (adapter emission vs the curated/frozen catalog),
   * the KB-1533 create-gate metric. MUST be declared here or the top-level schema (NO passthrough)
   * STRIPS it and the scorecard silently never renders — the same trap the `coverage` / `derivedPast` /
   * `offerUpdates` notes above document. Passthrough + optional: the reader (`readContentParity`)
   * normalizes defensively, and legacy / no-matched-offer docs simply lack it (→ no scorecard section).
   */
  contentParity: z.object({ matchedOffers: z.number().optional() }).passthrough().optional(),
  /**
   * KB-1738 — legacy-chain phase health (codegen / extractor / finalizer / reconcile) recorded by
   * the operator pipeline when the legacy per-camp chain runs. A `codegen: "failed"` on a
   * status:"success" run is the silent-legacy-fallback signature (say-it-with-glass /
   * carolina-friends-school / emerson-waldorf-school on the 2026-07-03 nightlies). MUST be
   * declared here or the top-level schema (NO passthrough) STRIPS it — the same trap the
   * `coverage` / `derivedPast` / `offerUpdates` / `contentParity` notes above document.
   * Passthrough + optional: adapter-native runs and legacy docs simply lack it.
   */
  legacyChain: z.object({ codegen: z.string().optional() }).passthrough().optional(),
  /**
   * KB-1768 — the per-run URL prefix table (`runDocLinkTable.cjs`): each member entry's `url`/`regUrl`
   * is persisted as `"<index>|<suffix>"` against this table so the KB-1739 reviewer links are cheap
   * enough for the size guard to keep on the largest runs (raleigh/cary). MUST be declared here or the
   * top-level schema (NO passthrough) STRIPS it — and every compressed link on the doc would silently
   * expand to nothing (`links.ts` omits an unresolvable ref). The same trap the `coverage` /
   * `derivedPast` / `offerUpdates` / `contentParity` notes above document. Optional: legacy docs and
   * runs with no links simply lack it (raw `url`/`regUrl` values pass through the expander untouched).
   */
  linkPrefixes: z.array(z.string()).optional(),
  /**
   * KB-2026 — recon's hard per-fetch-timeout accounting. `timedOutCount` is how many URLs were killed
   * at the ceiling (`hardTimeoutMs`, default 180s) after their one retry; `fetchedOkCount` is how many
   * pages actually came back. A run that limped in behind a wall of timeouts is DEGRADED, not merely
   * thin — surfacing the count is what keeps a half-harvest from reading as a small-but-healthy one.
   * (recon fails the run outright when timeouts ate EVERYTHING; see `shouldFailRunForFetchTimeouts`.)
   * MUST be declared here or the top-level schema (NO passthrough) STRIPS it and the count silently
   * never reaches the dashboard — the same trap the `coverage` / `derivedPast` / `offerUpdates` /
   * `contentParity` / `legacyChain` notes above document. Passthrough + optional: every run that
   * predates this, and every watch run, simply lacks it.
   */
  recon: z
    .object({
      hardTimeoutMs: z.number().optional(),
      timedOutCount: z.number().optional(),
      fetchedOkCount: z.number().optional(),
      /**
       * KB-2928 — recon's page budget ran out while URLs were still queued, i.e. this run harvested
       * a SUBSET of the camp. Same "tell degraded from merely thin" purpose as `timedOutCount`
       * directly above, and it rides the same lane deliberately rather than inventing a new
       * top-level key.
       */
      budgetExhausted: z.boolean().optional(),
      budgetEffectiveMaxPages: z.number().optional(),
      budgetQueuedAtExit: z.number().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-2928 — operator directives that could not take effect on this run.
   *
   * MUST be declared here or the top-level schema (NO passthrough) STRIPS it and the finding
   * silently never reaches the dashboard — the same trap the `coverage` / `derivedPast` /
   * `offerUpdates` / `contentParity` / `legacyChain` / `recon` notes above document. Round 1 of
   * this ticket's own review caught it being written by the worker as an undeclared top-level key:
   * an operator-visible signal that the client boundary erased, which is the ticket's own defect
   * wearing the shape of a fix for it.
   *
   * Passthrough + optional: every run predating this simply lacks it.
   */
  config: z
    .object({
      promptDirectiveDriftCount: z.number().optional(),
      promptDirectiveDrift: z
        .array(
          z
            .object({
              code: z.string().optional(),
              field: z.string().optional(),
              declared: z.string().optional(),
              stored: z.string().optional(),
              detail: z.string().optional(),
            })
            .passthrough(),
        )
        .optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3296 (written) / KB-3312 (declared here so run-details can READ it) — the field-gate counter block
   * `publishRunToFirestore.cjs` stamps onto the RUN doc (`enabled` + the six outcome tallies; `heldItems[]`
   * stripped, GCS-audit only). `.passthrough()` so the writer's fuller shape (skips / vendorRefs / weekly /
   * contributions counters — see `observed-to-offers.cjs`) survives; only what run-details reads is named.
   * `.optional()` and ABSENT when the writer predates the gate wiring — never a fabricated `enabled:false`
   * (Rule 4). Run-details reads `enabled` to decide whether to surface "held by the gate" at all.
   *
   * ⚠️ KB-3486 first added this declaration on `CampDataReviewPatternDocV1Schema` (the review-PATTERN doc),
   * not here — so the RUN doc, which `selectedParsed` parses through THIS schema (no top-level
   * `.passthrough()`), still stripped `gate` on read and every counter was invisible in production. KB-3312
   * moved it here (its correct home) and removed the dead pattern-doc copy (no reader accessed a pattern
   * doc's `.gate`); see the drift note on KB-3312.
   */
  gate: z
    .object({
      enabled: z.boolean(),
      heldLocked: z.number().optional(),
      heldRejected: z.number().optional(),
      heldBasis: z.number().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3561 — what the run withheld as UNKNOWN (a page it could not read, a session line it would not guess
   * at, an AMBIGUOUS_VARIATION row) and the offers it therefore kept out of the close passes. Written by
   * `publishRunToFirestore.cjs` (`buildWithheldUnknownForDoc`); rendered by `RunWithheldUnknownNotice`.
   * Declared here because this schema has no top-level `.passthrough()`: an undeclared key is stripped on
   * read and never reaches run details (the KB-3312 note above). Strings, not enums, for `kind`/`reason`/
   * `pass`, so a reason the writer adds later cannot fail the parse and blank the row.
   */
  withheldUnknown: z
    .object({
      sidecar: z.string().optional(),
      scopesTotal: z.number().optional(),
      scopesUnresolved: z.number().optional(),
      scopes: z
        .array(
          z
            .object({
              kind: z.string().optional(),
              locationText: z.string().optional(),
              pageUrl: z.string().optional(),
              reason: z.string().optional(),
              detail: z.string().optional(),
              startDays: z.array(z.string()).optional(),
              resolved: z.boolean().optional(),
            })
            .passthrough(),
        )
        .optional(),
      protectedTotal: z.number().optional(),
      protectedFromClose: z
        .array(
          z
            .object({
              docId: z.string().optional(),
              variationKey: z.string().optional(),
              startDateIso: z.string().optional(),
              pass: z.string().optional(),
              protectedBy: z.array(z.object({ kind: z.string().optional(), reason: z.string().optional() }).passthrough()).optional(),
            })
            .passthrough(),
        )
        .optional(),
      ambiguousRowsKeyed: z.number().optional(),
      ambiguousRowsUnkeyed: z.number().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3639 — the creation guards: first-seen rows judged, rows withheld (not created, proposed with
   * `withheldBy`), one counter per guard, and the same classes withheld earlier at match. Written by
   * `publishRunToFirestore.cjs` (`buildCreationGuardsForDoc`), every counter present as 0 on a quiet run.
   * Declared for the same reason as `withheldUnknown` above: an undeclared key is stripped on read.
   * `byGuard` / `atMatch` are records, so a guard added later reaches a reader without a schema change.
   * Both are optional: the publish prune (`stripUndefinedAndEmptyDeep`) drops an empty map, and a required
   * key there would fail the WHOLE run doc's parse and blank Run details (review round 1, finding 10).
   */
  creationGuards: z
    .object({
      evaluated: z.number(),
      withheld: z.number(),
      byGuard: z.record(z.string(), z.number()).optional(),
      atMatch: z.record(z.string(), z.number()).optional(),
      twinsProtectedFromClose: z.number().optional(),
      // KB-3698 — first-seen rows the guards passed, by price level (`session` / `programme`).
      passedByPriceLevel: z.record(z.string(), z.number()).optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-4306 — what terminal review cards absorbed this run, written by `publishRunToFirestore.cjs` after the
   * pattern upsert. `absorbed.approved.noCard` is the number that must read 0: offers whose rows merged into
   * an approved card its verdict did not cover, and that got no successor card (each with its reason).
   * Declared for the same stripping reason as `creationGuards`. Every leaf optional and the blocks
   * `.passthrough()`, so a counter added later cannot fail the whole run doc's parse.
   */
  reviewPatternSummary: z
    .object({
      schemaVersion: z.string().optional(),
      runId: z.string().optional(),
      absorbed: z
        .object({
          approved: z
            .object({
              offers: z.number().optional(),
              covered: z.number().optional(),
              successor: z.number().optional(),
              noCard: z.number().optional(),
              noCardReasons: z.record(z.string(), z.number()).optional(),
            })
            .passthrough()
            .optional(),
          rejected: z.object({ offers: z.number().optional() }).passthrough().optional(),
          suppressed: z.object({ offers: z.number().optional() }).passthrough().optional(),
          successorsCreated: z.number().optional(),
          successorsRefreshed: z.number().optional(),
          coverageReadFailed: z.number().optional(),
        })
        .passthrough()
        .optional(),
      priceCalcExplained: z
        .object({ suppressedCards: z.number().optional(), candidates: z.number().optional(), readFailed: z.boolean().optional() })
        .passthrough()
        .optional(),
    })
    .passthrough()
    .optional(),
});

export type CampDataRunDocV1 = z.infer<typeof CampDataRunDocV1Schema>;

export const CampDataGoldenDocV1Schema = z.object({
  schemaVersion: z.literal(CAMP_DATA_GOLDEN_DOC_SCHEMA_VERSION),
  campSlug: z.string().min(1),
  runId: z.string().min(1),
  setAtIso: z.string().min(1),
  note: z.string().optional(),
});
export type CampDataGoldenDocV1 = z.infer<typeof CampDataGoldenDocV1Schema>;

export const CAMP_DATA_REVIEW_PATTERN_SCHEMA_VERSION = "camp-data.reviewPattern.v1" as const;

/**
 * KB-2658 — one eligibility band on a proposal row, in the shape the pipeline already speaks
 * (`observedBandsFromForm` in offerComputeCore.cjs, and `variationData`).
 *
 * `.passthrough()` because a band is a projection of the variation's own field set: a future
 * pipeline field must reach the reviewer rather than being silently dropped at this boundary.
 * A grade key and its value travel as a coherent PAIR or not at all — the value is authoritative
 * (`gradeValueToKey` is a lossy inverse), so a value-less key is not something to assert about.
 */
const CampDataAgeBandSchema = z
  .object({
    minAgeInYears: z.number().optional(),
    maxAgeInYears: z.number().optional(),
    minAgeGradeKey: z.string().optional(),
    minAgeGradeValue: z.number().optional(),
    maxAgeGradeKey: z.string().optional(),
    maxAgeGradeValue: z.number().optional(),
  })
  .passthrough();

/**
 * KB-2874 — one adapter-raised "a human should look at this row" flag.
 *
 * Raised by the observe-layer adapter (`bondsports.adapter.cjs` today) and carried, never recomputed,
 * all the way to the item row an operator rules on. `kind` is what a counter or filter keys on;
 * `note` is the sentence the operator actually reads. `date` is present on a single-day flag,
 * `dates` on a span flag — they are not merged into one field because "lands on July 4th" and "spans
 * two days this camp is believed closed" are different claims and a reviewer acts on them differently.
 *
 * ⚠️ This is FLAG-ONLY. Nothing here suppresses, shortens or deletes a proposal — a flagged session
 * is proposed exactly like any other, with a label. The ticket's own analysis is why: a vendor may
 * run a short holiday week and still sell it, so suppressing the week would be the opposite defect,
 * and shortening it would manufacture a 3-day week carrying the 5-day price with no vendor statement
 * behind it.
 *
 * `.passthrough()` so a future adapter can add a field without this boundary silently eating it —
 * the KB-1509 / DEC-053 strip trap that this file's other notes cite.
 */
export const CampDataReviewFlagSchema = z
  .object({
    kind: z.string(),
    note: z.string().optional(),
    date: z.string().optional(),
    dates: z.array(z.string()).optional(),
    holiday: z.string().optional(),
  })
  .passthrough();

export type CampDataReviewFlag = z.infer<typeof CampDataReviewFlagSchema>;

/**
 * KB-1867 (TL-2) — `campDataReviewPatterns`: the cross-run judging unit for the review inbox (TL-5).
 * One doc per `(campSlug, changeGroupKey)`; docId = `sha1("${campSlug}|${changeGroupKey}")` (see
 * `functions/scripts/camp-data/_shared/derivePatternDocs.cjs`, the writer of record for field names).
 *
 * ⚠️ The top-level `z.object` STRIPS undeclared keys (the KB-1509 / DEC-053 trap). Every field the
 * nightly deriver writes AND every downstream-owned field (verdict, itemVerdicts, scheduleStatus,
 * scheduledApplyAtIso, applied, auditRequested, shapePrefixKey, previouslyRejected, chronicRecurrence)
 * is declared HERE, ONCE — complete on day one so a later ticket's field is never silently dropped on
 * read (CTO validation pass #2). `campData.test.ts` asserts a fully-populated doc round-trips unchanged.
 */
export const CampDataReviewPatternDocV1Schema = z.object({
  schemaVersion: z.literal(CAMP_DATA_REVIEW_PATTERN_SCHEMA_VERSION),

  // Identity + provenance (KB-1865 stamp).
  campSlug: z.string().min(1),
  vendorType: z.string(),
  changeGroupKey: z.string().min(1),
  groupKeyText: z.string(),
  fieldClass: z.string(),
  severity: z.enum(["HIGH", "LOW"]),
  // Numeric rank for an index-stable Inbox sort (0 = HIGH, 1 = LOW). Ordering by the string would rank
  // classes alphabetically by accident; the composite index (KB-1869) orders by severityRank asc.
  severityRank: z.number(),
  changeShape: z
    .object({
      shapeKind: z.union([z.enum(["cleared", "set", "changed"]), z.null()]).optional(),
      literal: z.union([z.string(), z.null()]).optional(),
    })
    .passthrough(),

  // Lifecycle.
  status: z.enum(["open", "approved", "rejected", "suppressed"]),
  /**
   * KB-2874 — `items` alone is a bare number. sport-hq's creation pattern read `{items: 500}`, and a
   * reviewer looking at it could not answer the only question that matters before approving 500
   * creations: is this all of them? (It was: 797 sessions harvested, 500 first-seen. Nothing was
   * truncated, but nothing on the card could say so.)
   *
   * `run*` are RUN-level — identical across every pattern of a run, hence the prefix; a bare `total`
   * beside `items` would read as this pattern's own total and be wrong. They are `nullable` because
   * the writer emits `null` when the audit could not supply them: a missing denominator must render
   * as unknown, never as a confident zero claiming the run harvested nothing.
   *
   * `runHarvestedRows` is `offerRows + skippedTransform` — the sessions that ENTERED the row builder,
   * not the ones that survived it. Round 1 finding 3: `offerRows` alone is post-skip, so a run that
   * lost 50 rows to unresolvable locations would have rendered "500 of 747 harvested" and silently
   * swallowed them behind a complete-sounding label. `runDroppedBeforeRow` surfaces that loss rather
   * than hiding it; 0 there is meaningful, `null` is "the audit could not say".
   *
   * ⚠️ There is deliberately NO "already on file" figure. Round 1 finding 2: the obvious source,
   * `counts.skippedNoChange`, means "matched an existing offer AND changed nothing" — an existing
   * offer with an availability flip lands in `updated` instead and is counted nowhere. The
   * 500 + 297 = 797 decomposition held on the sport-hq run only because that run had zero changes; on
   * the next ordinary run the card's arithmetic stops closing and the reviewer is back to tracing the
   * pipeline, behind a number that reads as authoritative. A figure that closes only on a lucky run
   * is worse than no figure.
   *
   * These keys already survived the boundary via `.passthrough()` — this declaration is a TYPE fix,
   * not a data fix, so `ReviewPatternCard` can read them without a cast.
   */
  counts: z
    .object({
      items: z.number(),
      runHarvestedRows: z.number().nullable().optional(),
      runDroppedBeforeRow: z.number().nullable().optional(),
      reviewFlagged: z.number().optional(),
      // KB-2950 — how many of this pattern's first-seen items would be CREATED with a blank
      // description (the `create-empty-description` guard, carried on `reviewFlags` and counted
      // separately from `reviewFlagged` in `derivePatternDocs.cjs`). `ReviewPatternCard` reads it to
      // render "N of M have no description" on the header, so the operator sees the gap before
      // approving rather than after. Optional: absent on any doc derived before this field existed.
      emptyDescription: z.number().optional(),
    })
    .passthrough(),
  // ≤5 sample items (DEC-053: item-level detail stays in GCS; this is a thin pointer sample).
  sampleItems: z.array(
    z
      .object({
        offerId: z.string().optional(),
        observedId: z.string().optional(),
        title: z.string().optional(),
        startDateIso: z.string().optional(),
        endDateIso: z.string().optional(),
        locationId: z.string().optional(),
        locationText: z.string().optional(), // KB-1890 — human venue text (shown instead of the raw locationId)
        url: z.string().optional(),
        regUrl: z.string().optional(),
        variationKey: z.string().optional(), // KB-1894 — reviewer info-density
        price: z.number().optional(), // KB-1894 — creation: preview.priceRegular; change: changedOffers.priceRegular
        // KB-2658 — the two age facts. `observedBand` is what the vendor page stated; `effectiveBand`
        // is the band the created/updated offer actually carries (post-merge `preview.variationData`
        // on a creation row, the stored offer's band on a change row). Both are OPTIONAL and their
        // ABSENCE IS MEANINGFUL — "the source said nothing" is a state the reviewer must see named,
        // never a zero. Declared explicitly rather than riding `.passthrough()` so the contract is
        // legible and survives a future tightening of this schema.
        observedBand: CampDataAgeBandSchema.optional(),
        effectiveBand: CampDataAgeBandSchema.optional(),
        // KB-2692 — the third age fact, and the one that says whether the other two are comparable.
        // When the matched variation sets `allowSessionAgeOverride`, `effectiveBand` above is an ECHO
        // of `observedBand` (the pipeline writes the observed age into `variationData`), so the two
        // agreeing means nothing. Absence is meaningful here too, and is NOT `false`: a pattern doc
        // written before this field existed records nothing either way, and a consumer must report
        // that row as unverified rather than assume the comparison was real.
        variationAllowsAgeOverride: z.boolean().optional(),
        // KB-2874 — the adapter's review flags, on the SAMPLE row. The card's flag line says "Open
        // the items to see which"; this is one of the three projections that has to carry the answer
        // for that sentence to be true (round 1 finding 1 — all three dropped it, so an operator told
        // "4 of 500 flagged" clicked through to 500 identical-looking rows). Absent when the adapter
        // flagged nothing — never an empty array, which would read as "checked and cleared" on a row
        // no adapter ever examined.
        reviewFlags: z.array(CampDataReviewFlagSchema).optional(),
        /**
         * KB-3414 (harvest plan §3, §4) — the HARVESTED CANDIDATE. A camp update scraped from the
         * camp's own site or read out of its newsletter enters this same review queue as a pattern
         * doc, and `sampleItems[0]` is where the candidate itself lives. Every key is optional, so
         * an offer-diff row — which carries none of them — parses exactly as it does today.
         *
         * ⚠️ KB-3414 filed these as needed because the schema strips undeclared keys. That is true
         * of the TOP-LEVEL object and not of this one: the row object below is `.passthrough()`, so
         * these keys would survive a round-trip undeclared. They are declared for the reason KB-2874
         * gave for `counts.runHarvestedRows` — a TYPE fix, not a data fix, so the Inbox card can read
         * a candidate without a cast, and so the contract is legible rather than inferred from a
         * writer. The keys the pattern doc must NOT lose are the ones that never reach the promoted
         * event: `sourceRef` here is the raw URL or message id and stays admin-read on this doc,
         * while the event's own `sourceRef` is the pattern doc path (§4).
         *
         * `effectiveAtIso` / `expiresAtIso` are ISO strings rather than Timestamps because every
         * other date on this row already is (`startDateIso`, `endDateIso`), and a mixed row is how
         * the noon-UTC-vs-ET-midnight illegibility in the Inbox got started.
         */
        kind: z.string().optional(),
        sourceKind: z.enum(["site_watch", "newsletter"]).optional(),
        summary: z.string().optional(),
        evidenceSpan: z.string().optional(),
        sourceRef: z.string().optional(),
        effectiveAtIso: z.string().optional(),
        expiresAtIso: z.string().optional(),
        links: z.array(z.string()).optional(),
        /**
         * The `sender` class only. Absence is meaningful and is NOT an empty array: nothing examined
         * this sender, which is a different state from "examined, matched no camp" — the distinction
         * the reviewer needs before confirming a sender against a camp.
         */
        senderCandidates: z
          .array(
            z
              .object({
                campLocId: z.string().optional(),
                orgId: z.string().optional(),
                matchedBy: z.string().optional(),
              })
              .passthrough(),
          )
          .optional(),
        /**
         * KB-3301 — the sampled item's field PROVENANCE: who set the current value, against what basis,
         * under what policy — so the inbox card renders "set by <actor> <date> against vendor <basis> ·
         * <policy>" with ZERO client reads of `fieldStates`. Attached by the nightly deriver's caller
         * (and by `getPatternItemsCore` for full pages) from the offer's `fieldStates.fields[k]` record;
         * `basis`'s value/runId/vendorType/observedAt come from the basis contribution
         * `fieldContributions/<basis.cid>` (dispatcher ruling 2026-09-13, for KB-3317's sub-line).
         *
         * Like `reviewFlags` above, these survive `.passthrough()` undeclared — this is a TYPE fix so the
         * card reads a sample without a cast, not a data fix. ABSENT when the offer has no governed
         * record for this field (Rule 4 — no standing rather than a fabricated one); `basis.value` absent
         * when the contribution is missing or its value spilled to GCS (`valueRef`), never an empty string.
         */
        provenance: z
          .object({
            policy: z.union([z.string(), z.null()]).optional(),
            canonical: z
              .object({
                valueHash: z.union([z.string(), z.null()]),
                sourceClass: z.union([z.string(), z.null()]),
                actorId: z.union([z.string(), z.null()]),
                at: z.union([z.string(), z.null()]),
                cid: z.union([z.string(), z.null()]),
              })
              .passthrough(),
            basis: z
              .object({
                cid: z.union([z.string(), z.null()]),
                valueHash: z.union([z.string(), z.null()]),
                judgedAt: z.union([z.string(), z.null()]),
                value: z.string().optional(),
                runId: z.string().optional(),
                vendorType: z.string().optional(),
                observedAt: z.string().optional(),
              })
              .passthrough()
              .optional(),
          })
          .passthrough()
          .optional(),
        from: z.unknown().optional(),
        to: z.unknown().optional(),
      })
      .passthrough(),
  ),
  itemsArtifact: z.union([z.string(), z.null()]).optional(),
  latestRunId: z.string(),
  firstSeenAtIso: z.string(),
  lastSeenAtIso: z.string(),
  recurrenceCount: z.number(),
  staleAsOfRunId: z.union([z.string(), z.null()]).optional(),
  // KB-2764 — the calibration-set stamp. The pipeline has written this since KB-2587
  // (`CALIBRATION_STAMP_FIELD`) and this schema never caught up; because this top-level object has no
  // `.passthrough()`, any consumer that starts parsing patterns would silently lose it. Declared now
  // because the card needs it: a frozen calibration member must never be visually de-emphasized, and
  // "is this a calibration member" is the only thing that can gate that.
  calibrationSetId: z.union([z.string(), z.null()]).optional(),

  /**
   * KB-4306 — successor cards. When a run's rows merge into a terminal APPROVED card for offers its verdict
   * did not cover, those offers go onto a successor: an open pattern with the SAME `changeGroupKey` (so its
   * approve applies through the normal engine) and a docId of its own. `successorOf` names the terminal card
   * it continues; `successorOfferIds` is the full offer list it was opened for (capped at 500);
   * `successorDocId` on the terminal card points at its latest successor. Declared because this top-level
   * object has no `.passthrough()`: an undeclared key is stripped on read, and the link would vanish.
   */
  successorOf: z
    .object({
      docId: z.string(),
      generation: z.number(),
      status: z.string().optional(),
      verdictAtIso: z.union([z.string(), z.null()]).optional(),
    })
    .passthrough()
    .optional(),
  successorOfferIds: z.array(z.string()).optional(),
  successorDocId: z.string().optional(),

  /**
   * KB-3414 (harvest plan §1, §5) — the retract stamp, written by the `retractCampActivity` callable
   * when a promoted announcement is withdrawn. Declared HERE because this top-level object has no
   * `.passthrough()`: an undeclared key is stripped on read, so the Inbox card would show a reopened
   * pattern with no record that it had ever been promoted and pulled — which reads exactly like a
   * pattern nobody has judged yet.
   *
   * `eventId` is nullable: a pattern can be retracted after a promotion that never landed (the
   * candidate was unpromotable), and null says that plainly rather than implying an event is out
   * there still showing.
   */
  /**
   * KB-3414 — the `campActivity` doc id the promoter wrote, recorded at promote time.
   *
   * Not derivable after the fact: the id hashes the candidate's summary and `sampleItems` is
   * refreshable, so a later run can change what a re-derivation would produce. Retract reads this to
   * expire the document that was actually written. Declared here because this top-level object has
   * no `.passthrough()` — an undeclared key is stripped on read, and a stripped one here means a
   * retract that silently expires nothing.
   */
  promotedEventId: z.string().optional(),
  /**
   * KB-3589 — every sibling's id when an announcement fanned out to two or more audience locations
   * (then `promotedEventId` is absent). Declared for the same stripping reason as `promotedEventId`.
   */
  promotedEventIds: z.array(z.string()).optional(),

  retracted: z
    .object({
      atIso: z.string(),
      byUid: z.string(),
      reason: z.string().optional(),
      eventId: z.union([z.string(), z.null()]).optional(),
      eventIds: z.array(z.string()).optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3588 — auto-publish. Declared for the same reason as `retracted` above: this top-level object has
   * no `.passthrough()`, so an undeclared key is stripped on read and the Inbox could not show why a card
   * stayed in pre-approval, or that it was published without a human.
   *
   * `autoPublishEligibility` is written by `onCampDataHarvestCardAutoPublish` on every open announcement
   * card it evaluates; `autoPublish` only on one it approved; `postReview` by `recordHarvestPostReview`.
   * `audience` is KB-3420's, read here for the post-review view.
   */
  autoPublishEligibility: z
    .object({
      eligible: z.boolean(),
      failing: z.array(z.object({ predicate: z.string(), reason: z.string() }).passthrough()),
      evaluatedAtIso: z.string().optional(),
    })
    .passthrough()
    .optional(),
  autoPublish: z
    .object({
      atIso: z.string(),
      sourceKind: z.string().optional(),
      holdKey: z.union([z.string(), z.null()]).optional(),
      orgId: z.union([z.string(), z.null()]).optional(),
      // KB-4376: `{day}__{orgId}__{audienceSetKey}`. Cards auto-published before it carry `orgDayKey` instead.
      audienceDayKey: z.union([z.string(), z.null()]).optional(),
      orgDayKey: z.union([z.string(), z.null()]).optional(),
    })
    .passthrough()
    .optional(),
  postReview: z
    .object({
      verdict: z.enum(["ok", "wrong"]),
      reason: z.union([z.string(), z.null()]).optional(),
      lesson: z.string().optional(),
      byUid: z.string().optional(),
      atIso: z.string(),
    })
    .passthrough()
    .optional(),
  audience: z
    .object({
      scope: z.string(),
      campLocIds: z.array(z.string()),
      basis: z.string().optional(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3034 — the expiry stamp, written by the nightly deriver from the FULL proposal-row set (never
   * from the ≤5 `sampleItems` above). Declared here because this top-level object has NO
   * `.passthrough()`: an undeclared key is stripped on read, so an unregistered `expiry` would be
   * written every night and be invisible to every consumer that parses through this schema.
   *
   * Counted over all rows, which is the entire point: the KB-3029 census could only PROVE expiry on
   * 1,280 patterns and had to SUGGEST it on 300 more holding 7,573 items behind five visible rows
   * each. The writer sees every row, so it dissolves that split.
   *
   * `optional()` because a doc derived before this field existed carries nothing, and that silence
   * must read as "unknown", never as "not expired" — `isShelvedPattern` is the one place allowed to
   * turn this block into a verdict, and it returns false on absence. The backfill is KB-3035.
   *
   * `allEnded` is true ONLY when `totalItems > 0`, `undatedItems === 0`, and every row's end day is
   * strictly before `asOfDay` — a session ending today is NOT ended. `newestEndDay` is null when no
   * row resolved a day. `.passthrough()` on the block (but not the doc) so the backfill can widen
   * `source` without a lockstep schema change.
   */
  expiry: z
    .object({
      allEnded: z.boolean(),
      endedItems: z.number(),
      undatedItems: z.number(),
      totalItems: z.number(),
      newestEndDay: z.union([z.string(), z.null()]),
      asOfDay: z.string(),
      asOfRunId: z.string(),
      source: z.string(),
    })
    .passthrough()
    .optional(),

  /**
   * KB-3301 — the doc-level provenance rollup, written by the nightly deriver's caller alongside the
   * per-sample `provenance` blocks. `basisKinds` is a histogram of the samples' canonical source class
   * (who set each value); it SUMS to the sample count, with `none` for a sample carrying no record and
   * `other` for a source class outside the four named (agent/script/report/unknown) — a sixth bucket the
   * ticket did not name but AC2's sum-invariant requires (a dropped class would silently miscount).
   *
   * `lockedExcluded` counts this run's `held-locked` contributions on the pattern's field — offers whose
   * vendor change was withheld because the field is locked, so the reviewer sees "N more locked". It is
   * `.optional()` and ABSENT when the gate did not run (Rule 4): a reassuring "0 locked excluded" over a
   * gate that never ran is the confident-absence this project exists to stop, never the same as a
   * measured zero.
   *
   * Declared HERE because this top-level object has NO `.passthrough()` — an undeclared key is stripped
   * on read (the KB-1509 trap), so an unregistered `provenanceSummary` would be written every night and
   * be invisible to every consumer that parses through this schema.
   */
  provenanceSummary: z
    .object({
      basisKinds: z
        .object({
          none: z.number(),
          staff: z.number(),
          director: z.number(),
          pipeline: z.number(),
          legacy: z.number(),
          other: z.number(),
        })
        .passthrough(),
      lockedExcluded: z.number().optional(),
    })
    .passthrough()
    .optional(),

  // Downstream-owned — declared now so the field-scoped nightly refresh never sees them stripped on read.
  verdict: z
    .union([
      z
        .object({
          verdict: z.string(),
          by: z.string(),
          byUid: z.string().optional(), // KB-2052 — authenticated caller (by stays "nicholas" for compat)
          channel: z.string().optional(), // KB-2052 — "reviewer-ui" | "remote-brief" (validator owns the enum)
          atIso: z.string(),
          inspectedCount: z.number().optional(),
          // KB-3597 — the org the operator chose on a sender card; the promoter binds the sender to it.
          senderSelection: z.object({ orgId: z.string() }).passthrough().optional(),
        })
        .passthrough(),
      z.null(),
    ])
    .optional(),
  itemVerdicts: z.record(z.string()).optional(), // KB-1868 — map of itemId → "approve"|"reject"
  scheduleStatus: z.string().optional(), // KB-1872 — "pending"|"applied"|"vetoed"|"approved-early"
  scheduledApplyAtIso: z.string().optional(), // KB-1872
  // KB-1872 — the trust scope the stamper resolved for this pattern ("writer:<vendorType>:<fieldClass>"),
  // persisted so the scheduled apply attributes its non-veto ledger row to the same scope that earned the
  // window, rather than re-deriving it from fields that may have been refreshed since.
  scopeKey: z.string().optional(),
  // KB-1872 — why a TIER-B pattern was withheld from the schedule (anomaly guard, or a structured value
  // whose per-item uniformity is unprovable). Only the surprising hold is stamped; "the switch is off" and
  // "the scope is tier d" are the boring defaults and are deliberately not persisted. Declared because
  // this top-level object has no .passthrough(): an undeclared key is stripped on read, which would make
  // the answer to "why didn't this apply?" invisible to the surface that has to answer it.
  scheduleHold: z
    .object({
      atIso: z.string(),
      reason: z.string(),
    })
    .passthrough()
    .optional(),
  // KB-1872 — the scheduled apply's own outcome stamp (distinct from KB-1870's `applied` summary, which
  // records the offer writes): what the tick did, and which ledger row attests to it.
  scheduleApplied: z
    .object({
      atIso: z.string(),
      applyStatus: z.string().optional(),
      appliedCount: z.number().optional(),
      verdictId: z.string().optional(),
    })
    .passthrough()
    .optional(),
  // KB-1870 writes an AppliedSummary OBJECT here ({atIso, applyMode, appliedCount, skippedStale,
  // skippedNoChange}), not a boolean — see applyPatternChanges.ts `patternRef.set({ applied: summary })`.
  // The original boolean-only declaration would have rejected every real applied doc the moment a
  // runtime consumer started parsing these (today only this file's own test does). Accept both: the
  // boolean form for any legacy doc, the summary object for what the apply engines actually write.
  applied: z.union([z.boolean(), z.object({}).passthrough()]).optional(), // KB-1870, widened KB-2086
  // KB-2086 — adopt-on-approve outcome stamp, written on EVERY outcome (applied / skipped / failed) so
  // "did my approve write anything?" is answerable. Declared here per this schema's day-one contract:
  // an undeclared key is stripped by the top-level z.object, which would make a failed adoption
  // invisible to the very reviewer surface that needs to see it.
  adoptOnApprove: z
    .object({
      atIso: z.string(),
      status: z.string(), // "applied" | "partial" | "skipped" | "failed"
      reason: z.string().optional(),
      error: z.string().optional(),
      applyMode: z.string().optional(),
      // KB-2382 fix round — the numeric outcome fields `describeAdoptOutcome` renders. Declared so the
      // stamp's contract lives in ONE place; a renamed key now shows up here, not as a silent `?? 0`.
      requested: z.number().optional(),
      appliedCount: z.number().optional(),
      created: z.number().optional(),
      alreadyExisted: z.number().optional(),
      failed: z.number().optional(),
      skippedStale: z.number().optional(),
      skippedNoChange: z.number().optional(),
      // KB-3239 — items declined because the field is off the KB-1798 appliable allowlist. The
      // fourth reachable outcome; before it existed a wholesale refusal was byte-identical to a lost
      // proposal artifact and `describeAdoptOutcome` rendered the latter.
      skippedNotAppliable: z.number().optional(),
      // KB-3473 — items dropped because the field is `policy:"locked"` (a director/staff lock). The
      // fifth reachable outcome; declared so `describeAdoptOutcome` can render "N locked, not applied"
      // instead of blaming the run artifact for a deliberate lock (the KB-3239 shape, one reason over).
      skippedLocked: z.number().optional(),
      // KB-4870 — items refused because the card's variationData names another variation than the offer's
      // variationKey. This block is `.passthrough()`, so the key survives undeclared; it is declared so the
      // stamp's contract stays in one place, per KB-2382 above. Same for skippedRejected (KB-3486), now carried.
      skippedVariationSplit: z.number().optional(),
      skippedRejected: z.number().optional(),
      // KB-3639 — creation rows a card approve left out because a creation guard withheld them; they
      // still need a per-item decision. The stamp's `reason` says so in words.
      withheldNotAdopted: z.number().optional(),
      // KB-4269 — the re-date path (`applyMode: "redate"`, a startDate/endDate card): one count per outcome,
      // and each skipped or waiting row with its own reason (capped; the counts are complete).
      redated: z.number().optional(),
      alreadyRedated: z.number().optional(),
      waiting: z.number().optional(),
      skippedRedate: z.number().optional(),
      skips: z.array(z.object({ offerId: z.string(), reason: z.string() })).optional(),
      waits: z.array(z.object({ offerId: z.string(), reason: z.string() })).optional(),
      waitingOnCards: z.array(z.string()).optional(),
      // KB-4304 — the variation-move path (`applyMode: "variation-move"`, a variationKey card): one count per
      // outcome; each refused row's reason is in `skips`.
      moved: z.number().optional(),
      alreadyMoved: z.number().optional(),
      skippedMove: z.number().optional(),
      planStartedAtIso: z.string().optional(),
      receiptId: z.string().optional(),
    })
    .passthrough()
    .optional(),
  /**
   * KB-1875 — the daily blind-audit draw's stamp. Was forward-declared as `z.boolean().optional()`
   * before the writer existed; widened here to what `campDataAuditSampleDraw` actually persists.
   *
   * Widening a declared field is normally the wrong move, so the check that licensed it: at the time
   * of the change `auditRequested` had NO reader and NO writer in either repo — only this line, the
   * round-trip fixture guarding it, and two comment mentions in `derivePatternDocs.cjs`. The
   * reservation reserved the NAME for this ticket, and the name is honoured; the type had no
   * dependents to migrate and no persisted doc has ever carried the boolean.
   *
   * The alternative — keep the boolean, put the detail in a second field — would leave two fields
   * asserting one fact, and the two can disagree. A stamp nothing writes is worse than no stamp: the
   * round-trip test would keep vouching for a shape no writer produces.
   *
   * `agentVerdictId` is the point of it: the verdict path derives `auditOf` — and the `disagreement`
   * flag the publishing halt reads — from THIS, so neither is ever a wire field a client could claim.
   *
   * Declared, where B1's `campyLease` / `campyEscalation` below are deliberately NOT — and the two
   * rulings share one rule rather than contradicting each other. The client may learn THAT an agent
   * acted; it may never learn WHAT the agent concluded. `campyEscalation` fails that (a decline is a
   * conclusion, rendered on the card the operator is about to rule on). This passes it: an opaque
   * verdict id carries no verdict, and it is visible only inside the "🎲 Audit sample" section, whose
   * whole premise is already "the agent decided these". `campyLease`'s hazard — agent activity
   * leaking into the UNLABELLED queue, where independence is assumed — does not arise for the same
   * reason. The reveal still happens only after the operator commits (`ReviewPatternCard`), so the
   * agreement statistic is computed over an unanchored vote.
   */
  auditRequested: z
    .object({
      dateIso: z.string(),
      agentVerdictId: z.string(),
    })
    .passthrough()
    .optional(),
  /**
   * KB-1875 — set when an audit vote contradicted the agent. Durable and queryable on purpose: the
   * amendment's halt keys on it, and the Inbox pins on it so the disagreement reappears at the top of
   * "Needs your eyes" rather than only existing as a ledger row nobody opens.
   */
  auditDisagreement: z
    .object({
      auditOf: z.string(),
      atIso: z.string(),
      agentVerdict: z.string(),
      operatorVerdict: z.string(),
    })
    .passthrough()
    .optional(),
  /**
   * KB-1875 — the audit was ANSWERED. Distinct from `auditRequested`, which only records that one was
   * asked for: without a separate marker the audit section could never clear, because a voted card and
   * an unvoted one would look identical on the doc.
   */
  auditCompleted: z
    .object({
      auditOf: z.string(),
      atIso: z.string(),
      verdictId: z.string(),
    })
    .passthrough()
    .optional(),
  shapePrefixKey: z.string().optional(), // KB-1871 — pre-literal shape prefix (sibling-find key)
  // KB-1871 — annotation on a new-literal doc reopened over a rejected sibling ("similar shape rejected …").
  previouslyRejected: z
    .object({
      groupKeyText: z.string(),
      literal: z.union([z.string(), z.null()]).optional(),
      rejectedAtIso: z.union([z.string(), z.null()]).optional(),
    })
    .passthrough()
    .optional(),
  chronicRecurrence: z.boolean().optional(), // KB-1871 — rejected pattern re-proposed ≥30 runs (fighting the adapter)
  //
  // ⚠️ KB-2583 (B1) — `campyLease` AND `campyEscalation` are written to this collection and are
  // DELIBERATELY NOT DECLARED HERE. Do not "complete" the schema by adding either.
  //
  // This schema's day-one contract is that every field a writer produces is declared so none is
  // silently dropped on read. `campyLease` is the deliberate exception, because dropping it on the
  // client is the desired behaviour, not an accident:
  //
  //   - `campyLease` is pure server-side coordination — which Campy session currently holds a
  //     pattern, and until when. It carries no judgement, so it is not the KB-1874 anchoring hazard
  //     directly. But surfacing it in the Inbox WOULD bias the corpus: if an operator can see that
  //     an agent is holding a pattern, the patterns humans judge stop being independent of agent
  //     activity, and the agreement statistic CO-SIGN promotion is gated on is computed over a
  //     skewed overlap. That is KB-1874's failure arriving through scheduling rather than through a
  //     rendered verdict.
  //   - `campyEscalation` is the STRONGER case, and the one to resist hardest. It records that an
  //     agent declined this (pattern, run) — which is agent judgement about the item, in the Inbox,
  //     on the card the operator is about to rule on. That is precisely what KB-1874's invisibility
  //     guard forbids: an anchored operator's verdict measures the agent against itself.
  //
  // Both are read only by the Cloud Functions that write them (Admin SDK, no Zod boundary), so the
  // strip costs nothing. If a future ticket genuinely needs either client-side, that is a decision
  // about operator anchoring first and a schema edit second.
  // KB-2041 — the one-off backfill's suppression stamp (`backfillSuppressOverrideClearPatterns.cjs`).
  // A suppressed pattern carries THIS instead of `verdict` (no human clicked approve/reject), so a
  // resolved-state surface reading "who/when" for a terminal pattern must fall back to it — declared
  // here per this schema's day-one contract, same reasoning as `adoptOnApprove` above.
  suppression: z
    .object({
      by: z.string(),
      atIso: z.string(),
      field: z.string().optional(),
      reason: z.string().optional(),
    })
    .passthrough()
    .optional(),
  // KB-3650 dispatcher ruling (Linear comment afd92b1c) — a fence artifact, not a KB-3650 feature.
  // KB-3635 writes `agentPanel` (judge-run publish) and reads it (the agent-apply gate) entirely
  // outside `src/**`, so this schema — which strips undeclared keys — is the one place left with no
  // other owner to declare it. `.passthrough()` because the panel's own shape is KB-3635's to evolve;
  // declaring it here only stops the key being silently dropped on read, per this schema's own day-one
  // contract above `suppression`. Nothing in this ticket reads or renders it.
  agentPanel: z
    .object({
      panelVersion: z.number(),
      runId: z.string(),
      raters: z.array(z.string()),
      agreed: z.boolean(),
      verdict: z.union([z.enum(["approve", "reject"]), z.null()]),
      confidence: z.number(),
    })
    .passthrough()
    .optional(),
});
export type CampDataReviewPatternDocV1 = z.infer<typeof CampDataReviewPatternDocV1Schema>;

export const CAMP_DATA_WATCHER_ALERT_SCHEMA_VERSION = "camp-data.watcherAlert.v1" as const;

/**
 * KB-1966 — `campDataWatcherAlerts/<slug>`: the cross-camp Inbox surface for dormant-camp WATCH runs.
 * One doc per watched camp (docId = campSlug), upserted by every watch run. `status:"signal"` means
 * the latest audit detected a reopening/change worth an operator's eyes (registration went live, the
 * "closed" notice vanished, a next-season CTA appeared, …); `status:"quiet"` means nothing changed.
 * The Inbox lists the open (`status=="signal"`, not acknowledged) alerts. Acknowledging is a
 * human action (re-onboard the camp, or dismiss) — `acknowledgedAtIso`/`acknowledgedBy` stamp it.
 * Like the run doc, this top-level object STRIPS undeclared keys on read — declare every field here.
 */
export const CampDataWatcherAlertDocV1Schema = z.object({
  schemaVersion: z.literal(CAMP_DATA_WATCHER_ALERT_SCHEMA_VERSION),
  campSlug: z.string().min(1),
  orgName: z.string().optional(),
  vendorType: z.string().optional(),
  status: z.enum(["quiet", "signal"]),
  // Derived: status=="signal" AND not yet acknowledged. The Inbox query is `where open==true` — a
  // single indexable field beats a status + null-acknowledged compound the client would post-filter.
  open: z.boolean(),
  level: z.union([z.enum(["critical", "warn", "info"]), z.null()]).optional(),
  signals: z.array(
    z
      .object({
        type: z.string(),
        severity: z.enum(["critical", "warn", "info"]),
        message: z.string(),
        evidence: z.record(z.unknown()).optional(),
        detectedAtIso: z.string().optional(),
      })
      .passthrough(),
  ),
  summaryMd: z.string().optional(),
  latestRunId: z.string(),
  firstSignalAtIso: z.union([z.string(), z.null()]).optional(),
  lastCheckedAtIso: z.string(),
  lastSignalAtIso: z.union([z.string(), z.null()]).optional(),
  // Human lifecycle — an operator who has seen a signal (and re-onboarded or dismissed it) clears it.
  acknowledgedAtIso: z.union([z.string(), z.null()]).optional(),
  acknowledgedBy: z.union([z.string(), z.null()]).optional(),
  acknowledgedRunId: z.union([z.string(), z.null()]).optional(),
});
export type CampDataWatcherAlertDocV1 = z.infer<typeof CampDataWatcherAlertDocV1Schema>;

// ---------------------------------------------------------------------------
// campDataOrgs/<campSlug> — the per-org camp-data wiring (KB-2004).
// ---------------------------------------------------------------------------

/**
 * The HARVEST axis: how (and whether) a camp's sessions are ingested.
 *
 * `"watch"` is the DEPRECATED pre-KB-2004 value — back then watch was a value of this enum, which
 * made watching and harvesting mutually exclusive. It is still read (a camp on the old shape keeps
 * watching through the harvest lane until it is migrated to `"none"` + `watch.mode:"on"`), but
 * nothing new should be set to it. The replacement is the orthogonal `watch` block below.
 */
export const CAMP_DATA_INGESTION_MODES = ["adapter-native", "pipeline", "none", "watch"] as const;
export type CampDataIngestionMode = (typeof CAMP_DATA_INGESTION_MODES)[number];

/**
 * The classification axis. `"legacy"` = harvested the old way and DELIBERATELY TERMINAL — not a
 * pending migration. Migration/coverage tooling reads this to stop counting the camp as work owed:
 * coverage judges a legacy camp on data freshness alone rather than on keeping the tight tracked
 * cadence we ask of camps still on the migration path (KB-2004 AC7).
 */
export const CAMP_DATA_CLASSIFICATIONS = ["legacy"] as const;
export type CampDataClassification = (typeof CAMP_DATA_CLASSIFICATIONS)[number];

/**
 * The WATCH axis (KB-2004) — independent of `ingestionMode`, with its OWN scheduler state, so a
 * camp can harvest sessions AND watch its site for a structural change at the same time.
 *
 * Two groups of fields, deliberately in one block:
 *   - TARGET config (KB-1966, pre-existing): `monitorUrls` / `vendorProbes` / `seasonYear` — WHAT
 *     the dormant-camp watcher probes. `runWatchAudit.cjs` THROWS without one of the first two.
 *   - SCHEDULER state (KB-2004, new): `mode` / `cadenceHours` / `nextRunAt` / `lastEnqueued*` /
 *     `lastError*` — WHEN the tick enqueues a watch job. The exact sibling of `autoTracker`, which
 *     drives the harvest cadence.
 *
 * `cadenceHours` is a bounded integer (1–168, default 72), NOT the harvest cadence enum
 * ([24,12,8,4,2]) — giving watch its own scheduler is what makes the intended 3-day cadence
 * reachable at all. Canonical validator: `normalizeWatchCadenceHours` in
 * functions/src/camp-data/autoTrackerEnqueuePolicy.ts.
 *
 * `.passthrough()` + all-optional: legacy org docs carry none of these fields and must normalize
 * cleanly, and the watcher's target config evolves independently of the scheduler's.
 */
/**
 * KB-1323 A — the page kinds a `monitorUrls` entry may carry. Canonical list:
 * `scripts/camp-data/_observed/watchAudit.cjs` `MONITOR_PAGE_KINDS`; `coverageRollup.ts`
 * `hasHarvestedMonitorUrl` reads the same two values to stamp `campLocations.watchedSources.site`.
 */
export const CAMP_DATA_MONITOR_PAGE_KINDS = ["announcement", "registration"] as const;
export type CampDataMonitorPageKind = (typeof CAMP_DATA_MONITOR_PAGE_KINDS)[number];

/**
 * One `watch.monitorUrls` entry. A bare URL string is ALERT-ONLY — fetched and diffed for signals,
 * never harvested — and is what every entry was before KB-1323. The object form opts the page into
 * harvest. `pageKind` is typed as a string because this schema describes stored docs and must never
 * reject one; the watcher reads an unknown kind as alert-only and logs it.
 */
export const CampDataMonitorUrlEntrySchema = z.union([
  z.string(),
  z.object({ url: z.string(), pageKind: z.string().optional() }).passthrough(),
]);
export type CampDataMonitorUrlEntry = z.infer<typeof CampDataMonitorUrlEntrySchema>;

export const CampDataOrgWatchSchema = z
  .object({
    // Scheduler (KB-2004).
    mode: z.enum(["on", "off"]).optional(),
    cadenceHours: z.number().int().min(1).max(168).optional(),
    /** Firestore Timestamp; typed loosely because this schema is shared with the client bundle. */
    nextRunAt: z.unknown().optional(),
    deltaMinutes: z.number().optional(),
    lastEnqueuedJobId: z.string().optional(),
    lastEnqueuedAtIso: z.string().optional(),
    lastError: z.string().optional(),
    lastErrorAtIso: z.string().optional(),
    // Target config (KB-1966).
    monitorUrls: z.array(CampDataMonitorUrlEntrySchema).optional(),
    vendorProbes: z.array(z.record(z.string(), z.unknown())).optional(),
    seasonYear: z.number().optional(),
  })
  .passthrough();
export type CampDataOrgWatch = z.infer<typeof CampDataOrgWatchSchema>;

/** A lane's last-run mirror — the freshness stamp `coverageRollup` classifies a camp by. */
export const CampDataOrgLastRunSchema = z
  .object({
    runId: z.string().optional(),
    endedAtIso: z.string().optional(),
    status: z.string().optional(),
    mode: z.string().optional(),
    watchStatus: z.string().optional(),
    /**
     * WHY the run named by `runId` harvested zero sessions, or `""` when it has no zero to explain
     * (KB-2305, KB-2636). Deliberately `z.string()` and not the run doc's enum: the mirror's
     * writers normalize an absent cause to `""` so that `set(..., {merge:true})` — which
     * deep-merges nested maps — overwrites a previous run's cause instead of inheriting it, and
     * `""` is not a member of that enum. Every consumer reads this with a truthiness test, so `""`
     * and absent are equivalent downstream (`coverageRollup.ts`). It was written here since
     * KB-2305 and carried only by `.passthrough()`; declaring it makes the contract legible.
     */
    zeroSessionsCause: z.string().optional(),
  })
  .passthrough();

/**
 * `campDataOrgs/<campSlug>` — a camp's camp-data wiring, on TWO ORTHOGONAL AXES (KB-2004):
 *
 *   HARVEST — `ingestionMode` (+ `autoTracker` cadence, `lastRun` freshness mirror)
 *   WATCH   — `watch` (+ its own cadence and `lastWatchRun` freshness mirror)
 *
 * The two mirrors are separate on purpose: a slow watch run must never overwrite the harvest's
 * last-run stamp, or a dual-lane camp whose harvest had gone stale would keep reading as freshly
 * run and coverage would report it green off a run that harvested nothing.
 *
 * Everything is optional + `.passthrough()`: this schema DESCRIBES docs that predate it (no field
 * is guaranteed present) and must never reject one. It is a read-side contract, not a write gate.
 */
export const CampDataOrgDocSchema = z
  .object({
    // HARVEST axis.
    ingestionMode: z.enum(CAMP_DATA_INGESTION_MODES).optional(),
    autoTracker: z.record(z.string(), z.unknown()).optional(),
    lastRun: CampDataOrgLastRunSchema.optional(),
    // WATCH axis.
    watch: CampDataOrgWatchSchema.optional(),
    lastWatchRun: CampDataOrgLastRunSchema.optional(),
    // Classification axis.
    classification: z.enum(CAMP_DATA_CLASSIFICATIONS).optional(),
    // Common metadata.
    orgId: z.string().optional(),
    orgName: z.string().optional(),
    vendorType: z.string().optional(),
    updatedAtIso: z.string().optional(),
  })
  .passthrough();
export type CampDataOrgDoc = z.infer<typeof CampDataOrgDocSchema>;

/**
 * True when a camp runs the independent WATCH lane.
 *
 * The `ingestionMode !== "watch"` guard is the back-compat exclusivity rule: a camp on the legacy
 * shape watches through its HARVEST lane, so if it also matched the new lane it would be watched
 * twice per cycle. Exactly one lane ever watches a given camp.
 *
 * Mirrors `isWatchLaneActive` in functions/src/camp-data/autoTrackerEnqueuePolicy.ts (the scheduler
 * can't import from the client bundle); keep the two in step.
 */
export function isCampDataWatchActive(org: Pick<CampDataOrgDoc, "ingestionMode" | "watch"> | null | undefined): boolean {
  if (!org) return false;
  if (String(org.ingestionMode ?? "").trim() === "watch") return false;
  return String(org.watch?.mode ?? "").trim().toLowerCase() === "on";
}

export function campDataRunDocId(campSlug: string, runId: string) {
  return `${campSlug}__${runId}`;
}

export function isoNoMsNow() {
  return new Date().toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function truncateBlob(text: string, maxChars = 120_000) {
  const s = String(text ?? "");
  if (s.length <= maxChars) return s;
  return s.slice(0, Math.max(0, maxChars - 20)) + "\n\n[truncated]";
}

export function stripUndefinedDeep<T>(value: T): T {
  // Removes any `undefined` values (Firestore disallows them).
  if (Array.isArray(value)) {
    // Also drop undefined entries from arrays.
    return value
      .filter((v) => v !== undefined)
      .map((v) => stripUndefinedDeep(v)) as T;
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (v === undefined) continue;
      out[k] = stripUndefinedDeep(v);
    }
    return out as T;
  }
  return value;
}

export type ParsedRunJsonExecutionSummary = NonNullable<CampDataRunDocV1["execution"]>;

function toIsoNoMsMaybe(s: unknown): string | undefined {
  const t = String(s ?? "").trim();
  if (!t) return undefined;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.toISOString().replace(/\.\d{3}Z$/, "Z");
}

export function parseRunJsonToExecutionSummary(runJson: unknown): ParsedRunJsonExecutionSummary | null {
  if (!runJson || typeof runJson !== "object") return null;
  const exec = (runJson as any).execution;
  if (!exec || typeof exec !== "object") return null;

  const stages: Record<string, { startedAtIso?: string; endedAtIso?: string }> = {};
  for (const [stage, info] of Object.entries(exec as Record<string, any>)) {
    if (!info || typeof info !== "object") continue;
    const startedAtIso = toIsoNoMsMaybe(info.startedAt);
    const endedAtIso = toIsoNoMsMaybe(info.endedAt);
    if (startedAtIso || endedAtIso) stages[stage] = { startedAtIso, endedAtIso };
  }

  const runStartedAtIso =
    toIsoNoMsMaybe((runJson as any).runStartedAt) ||
    toIsoNoMsMaybe(exec.extractor?.startedAt) ||
    toIsoNoMsMaybe(exec.finalizer?.startedAt);
  const runEndedAtIso =
    toIsoNoMsMaybe((runJson as any).runEndedAt) ||
    toIsoNoMsMaybe(exec.qc?.endedAt) ||
    toIsoNoMsMaybe(exec.match?.endedAt) ||
    toIsoNoMsMaybe(exec.finalizer?.endedAt) ||
    toIsoNoMsMaybe(exec.extractor?.endedAt);

  return {
    runStartedAtIso,
    runEndedAtIso,
    stages: Object.keys(stages).length ? stages : undefined,
  };
}

export function inferRunStatusFromDoc(doc: Partial<CampDataRunDocV1>): CampDataRunStatus {
  // Run status reflects pipeline outcome. Proposals are a separate "action required" signal.
  if (doc.qc?.regressionStatus === "FAIL") return "fail";
  if (doc.qc?.regressionStatus === "PASS") return "success";
  return (doc.status as CampDataRunStatus) || "unknown";
}

export function hasProposalBlockers(doc: Partial<CampDataRunDocV1>): boolean {
  return Boolean(doc.proposals?.blocked);
}

export function blockedSessionsCountFromDoc(doc: Partial<CampDataRunDocV1>): number {
  const loc = Number(doc.proposals?.counts?.location ?? 0) || 0;
  const variation = Number(doc.proposals?.counts?.variation ?? 0) || 0;
  return Math.max(0, loc + variation);
}

